import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { MoodEntry } from '@/types/mood';

export function useMoodEntries() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<MoodEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [todayEntry, setTodayEntry] = useState<MoodEntry | null>(null);

  const fetchEntries = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);

    const { data, error } = await (supabase as any)
      .from('mood_entries')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(30);

    if (error) {
      console.error('Error fetching mood entries:', error);
    } else {
      const moodEntries = (data as MoodEntry[]) || [];
      setEntries(moodEntries);

      // Check if there's an entry from today
      const today = new Date().toDateString();
      const todaysMood = moodEntries.find(
        (entry) => new Date(entry.created_at).toDateString() === today
      );
      setTodayEntry(todaysMood || null);
    }
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchEntries();
    } else {
      setIsLoading(false);
    }
  }, [user, fetchEntries]);

  const createEntry = async (
    moodValue: number,
    moodLabel: string,
    isPublic: boolean = false,
    notes?: string
  ): Promise<MoodEntry | null> => {
    if (!user) return null;

    const { data, error } = await (supabase as any)
      .from('mood_entries')
      .insert({
        user_id: user.id,
        mood_value: moodValue,
        mood_label: moodLabel,
        is_public: isPublic,
        notes: notes || null,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating mood entry:', error);
      throw new Error('Failed to save mood');
    }

    const newEntry = data as MoodEntry;
    setEntries((prev) => [newEntry, ...prev]);
    setTodayEntry(newEntry);
    return newEntry;
  };

  const updateEntry = async (
    id: string,
    updates: Partial<Pick<MoodEntry, 'mood_value' | 'mood_label' | 'is_public' | 'notes'>>
  ): Promise<MoodEntry | null> => {
    if (!user) return null;

    const { data, error } = await (supabase as any)
      .from('mood_entries')
      .update(updates)
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating mood entry:', error);
      throw new Error('Failed to update mood');
    }

    const updatedEntry = data as MoodEntry;
    setEntries((prev) => prev.map((e) => (e.id === id ? updatedEntry : e)));
    
    const today = new Date().toDateString();
    if (new Date(updatedEntry.created_at).toDateString() === today) {
      setTodayEntry(updatedEntry);
    }
    
    return updatedEntry;
  };

  const deleteEntry = async (id: string): Promise<boolean> => {
    if (!user) return false;

    const { error } = await (supabase as any)
      .from('mood_entries')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error deleting mood entry:', error);
      throw new Error('Failed to delete mood');
    }

    setEntries((prev) => prev.filter((e) => e.id !== id));
    
    if (todayEntry?.id === id) {
      setTodayEntry(null);
    }
    
    return true;
  };

  return {
    entries,
    todayEntry,
    isLoading,
    createEntry,
    updateEntry,
    deleteEntry,
    refetch: fetchEntries,
  };
}
